import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, ArrowRight, BatteryCharging, ShieldCheck, Gauge, Wrench, CheckCircle, AlertTriangle, Leaf } from "lucide-react";
import { getBusinessConfig } from "@/lib/site-config";
import { seoHead, serviceSchema, localBusinessSchema, complianceToBusinessOpts } from "@/lib/seo";
import { faqSchema, FAQAccordion } from "@/components/FAQAccordion";
import { ReassuranceBanner } from "@/components/ReassuranceBanner";
import { CTABlock } from "@/components/CTABlock";
import { AnimatedSection, StaggerContainer, StaggerItem } from "@/components/AnimatedSection";
import { PrimaryCTA } from "@/components/PrimaryCTA";
import { getPublicConfig } from "@/lib/site-content.functions";
import { ServiceTestimonials } from "@/components/ServiceTestimonials";
import { InternalLinksBlock } from "@/components/InternalLinksBlock";

const faqs = [
  {
    q: "Can you service hybrid and plug-in hybrid (PHEV) cars?",
    a: "Yes. We service self-charging hybrids (Toyota, Lexus, Honda) and plug-in hybrids (Mitsubishi Outlander PHEV, BMW iPerformance, Volvo Recharge, Range Rover P400e and similar). Hybrids still have an engine, gearbox, brakes, suspension and 12V system that need regular attention — we look after the conventional side and inspect the high-voltage components for visible faults.",
  },
  {
    q: "Do you service fully electric cars (EVs)?",
    a: "We carry out routine EV servicing — cabin filter, brake fluid, coolant top-up where applicable, suspension and steering checks, tyres, wipers, 12V battery health, software-free visual checks of the high-voltage cabling, and a full safety inspection. For high-voltage battery diagnostics, motor strip-downs or manufacturer software updates we will refer you to a marque specialist or main dealer — we are honest about what is safe to do in an independent workshop.",
  },
  {
    q: "Are your technicians qualified to work on high-voltage systems?",
    a: "Any work that requires the high-voltage system to be isolated is only carried out by an IMI Level 3/4 qualified technician. For routine non-HV work (tyres, brakes, suspension, 12V, cabin filter, fluids) standard automotive qualifications are sufficient and the high-voltage system stays untouched.",
  },
  {
    q: "Do EVs and hybrids need an MOT?",
    a: "Yes — EVs and hybrids need an annual MOT from the third anniversary of first registration, exactly like petrol and diesel cars. The MOT itself does not test the high-voltage battery, but everything else (brakes, tyres, lights, suspension, body condition, wipers, seatbelts) is tested as normal.",
  },
  {
    q: "Why do EV and hybrid brakes last so long?",
    a: "Regenerative braking does most of the slowing on an EV or hybrid, so the friction brakes are used less. This is great for pad and disc life, but it also means the discs can corrode and the calipers can seize from under-use. We clean, lubricate and free off the caliper slides as part of every EV/hybrid service.",
  },
  {
    q: "Does the 12V battery still matter on an EV?",
    a: "Yes — and it is the single most common reason for an EV breakdown. The 12V battery wakes the high-voltage system; if it dies, the car will not start even with a full traction battery. We test the 12V battery health on every service and replace it before it strands you.",
  },
  {
    q: "Will servicing with you affect my manufacturer warranty?",
    a: "No. Under UK Block Exemption rules you can have your car serviced at any competent independent garage using parts of equivalent quality without losing the manufacturer warranty. We stamp the service book and keep a digital record.",
  },
];

export const Route = createFileRoute("/services/ev-hybrid-servicing")({
  loader: async () => {
    const cfg = await getPublicConfig().catch(() => null);
    return { compliance: cfg?.compliance || null };
  },
  head: ({ loaderData }) =>
    seoHead({
      path: "/services/ev-hybrid-servicing",
      title: "EV & Hybrid Servicing East London E14 | Woodseer Autos",
      description:
        "Independent EV and hybrid servicing in Poplar, E14. Routine servicing for electric and hybrid cars — brakes, tyres, 12V, cabin filter, safety checks. Honest about HV work.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Services", path: "/services" },
        { name: "EV & Hybrid Servicing", path: "/services/ev-hybrid-servicing" },
      ],
      jsonLd: [
        localBusinessSchema(complianceToBusinessOpts(loaderData?.compliance)),
        faqSchema(faqs),
        serviceSchema({
          name: "EV & Hybrid Servicing — Woodseer Autos",
          serviceType: "Electric and Hybrid Vehicle Servicing",
          description:
            "Routine servicing for EVs, self-charging hybrids and plug-in hybrids in East London — brakes, tyres, 12V battery, cabin filter, suspension and safety inspection.",
        }),
      ],
    }),
  component: EVHybridServicingPage,
});

const INCLUDED = [
  "Full multi-point safety inspection (lights, wipers, seatbelts, body)",
  "Tyre condition, tread depth and pressures (including spare/repair kit)",
  "Brake pad, disc and caliper inspection — clean and lubricate slides",
  "Brake fluid moisture test and top-up or change as required",
  "12V auxiliary battery health and charge-state test",
  "Cabin / pollen filter inspection and replacement if needed",
  "Suspension, steering and underbody visual inspection",
  "Coolant level check (battery, inverter and cabin circuits where fitted)",
  "Wiper blades and washer system top-up",
  "Visual inspection of high-voltage cabling and connectors (no HV intervention)",
  "Digital service record and stamped service book",
];

const SIGNS = [
  "Reduced electric-only range or faster battery drain than usual",
  "12V battery warning, slow wake-up or 'car not ready' messages",
  "Spongy or pulsating brake pedal, or grinding from rarely-used brakes",
  "Regenerative braking feels weaker or inconsistent",
  "Charging is slower than normal or stops part-way through a session",
  "Coolant or yellow battery warning on the dashboard",
  "Knocking or clunks from suspension when crossing speed bumps (EVs are heavy — bushes and arms wear faster)",
];


function EVHybridServicingPage() {
  const biz = getBusinessConfig();

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-secondary/40 to-background border-b border-border">
        <div className="mx-auto max-w-5xl px-4 py-14 md:py-20">
          <p className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs font-bold text-primary uppercase tracking-[0.18em]">
            <Leaf className="h-3.5 w-3.5" /> EV & Hybrid
          </p>
          <h1 className="mt-5 display-lg text-foreground">
            EV &amp; Hybrid Servicing in East London
          </h1>
          <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Independent servicing for electric, self-charging hybrid and plug-in hybrid cars at
            our Poplar workshop. We look after the parts that wear — brakes, tyres, 12V battery,
            suspension and cabin systems — and we are honest about which jobs belong with a
            marque specialist. No upselling, no scare stories, written quotes before any work.
          </p>
          <PrimaryCTA location="ev-hybrid-hero" intent="EV/hybrid service quote" variant="light" />
          <div className="mt-4">
            <a
              href="#faqs"
              className="inline-flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-primary hover:underline underline-offset-4"
            >
              Jump to EV &amp; hybrid FAQs <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <ReassuranceBanner />

      {/* Who this is for */}
      <AnimatedSection>
        <section className="py-12 md:py-16">
          <div className="mx-auto max-w-5xl px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
              Who this service is for
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl">
              We routinely service the cars East London drivers actually own — from a Toyota Prius
              or Yaris Hybrid daily, to a Nissan Leaf, Tesla Model 3, Polestar 2, MG4, Kia Niro EV,
              Hyundai Ioniq 5, BMW i4, Volvo XC40 Recharge, and PHEVs like the Range Rover P400e or
              Mitsubishi Outlander PHEV. If your car has a plug, a high-voltage badge or a green
              flash on the number plate — we can help.
            </p>
            <StaggerContainer className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: BatteryCharging, title: "Full EVs", desc: "Tesla, Polestar, MG, Kia, Hyundai, Nissan, BMW i, Mercedes EQ, Volvo Recharge and more." },
                { icon: Gauge, title: "Self-charging hybrids", desc: "Toyota, Lexus, Honda and Ford hybrids — engine, gearbox, brakes and 12V looked after properly." },
                { icon: Wrench, title: "Plug-in hybrids (PHEV)", desc: "Range Rover, Mitsubishi, BMW iPerformance, Volvo, Mercedes and VAG plug-in hybrids." },
              ].map((c) => (
                <StaggerItem key={c.title}>
                  <div className="surface-premium rounded-xl p-5 h-full">
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <c.icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-4 text-base font-bold text-foreground">{c.title}</h3>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      {/* What's included */}
      <section className="py-12 md:py-16 bg-secondary/30 border-y border-border">
        <div className="mx-auto max-w-5xl px-4 grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
              What's included in an EV / hybrid service
            </h2>
            <p className="mt-3 text-muted-foreground">
              Electric and hybrid cars do not need oil and filter changes, but they still wear out
              the same chassis, tyre, brake and 12V components as a petrol car — often faster
              because they are heavier and torquier. Here's what we check on every visit.
            </p>
          </div>
          <ul className="space-y-2.5">
            {INCLUDED.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-sm text-foreground">
                <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Signs your EV needs attention */}
      <AnimatedSection>
        <section className="py-12 md:py-16">
          <div className="mx-auto max-w-5xl px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
              Signs your EV or hybrid is due a service
            </h2>
            <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
              {SIGNS.map((s) => (
                <li
                  key={s}
                  className="flex items-start gap-2.5 rounded-lg border border-border bg-card p-4 text-sm text-foreground"
                >
                  <AlertTriangle className="h-4 w-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </AnimatedSection>

      {/* Honest about HV */}
      <section className="py-12 md:py-16 bg-secondary/30 border-y border-border">
        <div className="mx-auto max-w-3xl px-4">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-6 w-6 text-primary shrink-0 mt-1" aria-hidden="true" />
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                Honest about high-voltage work
              </h2>
              <p className="mt-3 text-muted-foreground leading-relaxed">
                Anything that requires isolating the high-voltage (HV) system — traction battery
                replacement, motor or inverter strip-down, manufacturer software updates — is
                specialist work. We will tell you up front when a job belongs with a marque
                specialist or a main dealer rather than charge you to find out. For routine
                servicing, brakes, suspension, tyres, 12V and safety checks, we are the
                independent local option for East London EV and hybrid drivers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section id="faqs" aria-labelledby="ev-hybrid-faqs-heading" className="py-12 md:py-16 scroll-mt-24">
        <div className="mx-auto max-w-3xl px-4">
          <h2 id="ev-hybrid-faqs-heading" className="sr-only">EV & hybrid servicing FAQs</h2>
          <FAQAccordion faqs={faqs} title="EV & hybrid servicing — FAQs" />

          {/* FAQ-driven CTA */}
          <div className="mt-8 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/8 via-card to-card p-6 md:p-7">
            <h3 className="text-lg md:text-xl font-bold text-foreground tracking-tight">
              Still have questions about your EV or hybrid?
            </h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              Tell us the make, model and what you've noticed — we'll give you an honest written quote
              before any work starts. No upselling, no scare stories.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <a
                href={biz.phoneHref}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground hover:bg-primary/90 transition-colors"
                aria-label={`Call Woodseer Autos on ${biz.phone}`}
              >
                <Phone className="h-4 w-4" aria-hidden="true" /> Call {biz.phone}
              </a>
              <Link
                to="/book"
                className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-5 py-2.5 text-sm font-bold text-foreground hover:bg-secondary transition-colors"
              >
                Book EV / hybrid service <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-primary hover:underline underline-offset-4"
              >
                Ask a question <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <ServiceTestimonials title="What East London drivers say" />

      <InternalLinksBlock showHubLink />

      <CTABlock />
    </>
  );
}
